import { useCallback, useEffect, useMemo, useState } from 'react';
import { getRequestData, submitInvoiceByManager } from 'api/api';
import { endpoints } from 'api/endpoints';
import GlobalModal from 'components/globalModal';
import { SingleInvoiceRow } from './singleInvoiceRow';
import { Pagination } from 'antd';
import DynamicRows from './dynamicRows';
import { ROLE_TYPES } from 'api/constants';
import { IconDownload, IconReset, IconSmallArrow, OrangeButton, SelectOption, SmallButton } from 'components';
import { COLORS } from 'utils/colors';
import SlhSelect from 'components/slhSelect';
import { openUrlWithRole } from 'utils/helpers';
import hockeyGoalImage from 'assets/images/hockey-goal.png';

export const InvoiceHistory = ({
  isOpen,
  onClose,
  role,
  partnerName,
  partnerAddress,
  selectedPartner,
  partnersData = [],
}) => {
  const [invoices, setInvoices] = useState([]);
  const [periodOfInvoices, setPeriodOfInvoices] = useState('weekly');
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [partnersList, setPartnersList] = useState(Array.isArray(partnersData) ? partnersData : []);
  const [selectedModalPartner, setSelectedModalPartner] = useState(selectedPartner ?? null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, order: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [invoiceMode, setInvoiceMode] = useState('preview');

  const chosenPartner = useMemo(() => selectedModalPartner ?? null, [selectedModalPartner]);

  const isFiltersActive =
    selectedModalPartner !== null ||
    statusFilter !== 'all' ||
    (role === ROLE_TYPES.admin && periodOfInvoices !== 'weekly');

  useEffect(() => {
    if (Array.isArray(partnersData) && partnersData.length) {
      setPartnersList(partnersData);
    }
  }, [partnersData]);

  useEffect(() => {
    if (isOpen) {
      setSelectedModalPartner(selectedPartner ?? null);
    }
  }, [isOpen, selectedPartner]);

  useEffect(() => {
    if (partnersList.length) return;

    (async () => {
      const { data } = await getRequestData(endpoints.getAllPartners(), role);
      if (data) {
        setPartnersList(data);
      }
    })();
  }, [partnersList.length, role]);

  const normalizeStatus = (status) => String(status || '').toLowerCase();

  const getSortableValue = useCallback((invoice, key) => {
    if (key === 'invoice_id') return Number(invoice.invoice_id || 0);
    if (key === 'week_start') return new Date(invoice.week_start || 0).getTime();
    if (key === 'week_end') return new Date(invoice.week_end || 0).getTime();
    if (key === 'partner_name') return String(invoice?.partner?.name || '').toLowerCase();
    if (key === 'active_clients') return Number(invoice.active_clients || 0);
    if (key === 'total') return Number(invoice.total || 0);
    if (key === 'status') return normalizeStatus(invoice.status);
    return '';
  }, []);

  const sortInvoices = useCallback(
    (results) => {
      if (!sortConfig.key) return results;

      const direction = sortConfig.order === 'asc' ? 1 : -1;
      return [...results].sort((a, b) => {
        const aValue = getSortableValue(a, sortConfig.key);
        const bValue = getSortableValue(b, sortConfig.key);
        if (aValue > bValue) return direction;
        if (aValue < bValue) return -direction;
        return 0;
      });
    },
    [getSortableValue, sortConfig.key, sortConfig.order],
  );

  const getInvoiceData = useCallback(async () => {
    let data;

    if (chosenPartner) {
      const params = {
        period: periodOfInvoices,
        page: currentPage,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await getRequestData(
        endpoints.getWeeklyClientInvoices(chosenPartner, params),
        role,
      );
      data = response?.data;
    } else {
      const params = {
        period: periodOfInvoices,
        page: currentPage,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (sortConfig.key) {
        params.sort_by = sortConfig.key;
        params.sort_order = sortConfig.order;
      }

      const response = await getRequestData(endpoints.getAllInvoices(params), role);
      data = response?.data;
    }

    if (!data) return;

    let filtered = Array.isArray(data.results) ? [...data.results] : [];

    if (statusFilter !== 'all') {
      filtered = filtered.filter((invoice) => normalizeStatus(invoice.status) === statusFilter);
    }

    const sorted = sortInvoices(filtered);

    setInvoices({
      ...data,
      results: sorted,
    });
  }, [
    chosenPartner,
    currentPage,
    periodOfInvoices,
    role,
    sortConfig.key,
    sortConfig.order,
    sortInvoices,
    statusFilter,
  ]);

  useEffect(() => {
    getInvoiceData();
  }, [getInvoiceData]);

  const handlePaginationChange = (page) => {
    setCurrentPage(page);
  };

  const handlePeriodChange = (period) => {
    setPeriodOfInvoices(period);
    setCurrentPage(1);
  };

  const handlePartnerFilterChange = (partner) => {
    setSelectedModalPartner(partner);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status || 'all');
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSelectedModalPartner(null);
    setStatusFilter('all');
    setPeriodOfInvoices('weekly');
    setCurrentPage(1);
  };

  const handleSortChange = (key) => {
    setCurrentPage(1);
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return { key, order: 'asc' };
      }

      return {
        key,
        order: prev.order === 'asc' ? 'desc' : 'asc',
      };
    });
  };

  const handleBackToList = () => {
    setActiveInvoice(null);
    setInvoiceMode('preview');
  };

  const handleBackToPreview = () => {
    setInvoiceMode('preview');
  };

  const openActiveInvoiceFile = useCallback(async () => {
    if (!activeInvoice) return;

    if (activeInvoice.url) {
      openUrlWithRole(activeInvoice.url, role);
      return;
    }

    const { data } = await getRequestData(endpoints.exportInvoice(activeInvoice.invoice_id), role);
    if (data?.url) {
      openUrlWithRole(data.url, role);
    }
  }, [activeInvoice, role]);

  const activeInvoiceStatus = String(activeInvoice?.status || '').toLowerCase();

  const handleSubmitActiveInvoice = async () => {
    if (!activeInvoice || role !== ROLE_TYPES.slh_manager) return;

    await submitInvoiceByManager(activeInvoice.invoice_id, async () => {
      await getInvoiceData();
      handleBackToList();
    });
  };

  const handleSubmitInvoice = useCallback(async (invoice) => {
    if (!invoice || role !== ROLE_TYPES.slh_manager) return;

    await submitInvoiceByManager(invoice.invoice_id, async () => {
      await getInvoiceData();
      if (activeInvoice?.invoice_id === invoice.invoice_id) {
        handleBackToList();
      }
    });
  }, [activeInvoice?.invoice_id, getInvoiceData, role]);

  const renderSortableHeader = (label, key, className) => {
    const isActive = sortConfig.key === key;
    return (
      <button
        type="button"
        onClick={() => handleSortChange(key)}
        className={`flex items-center justify-start gap-x-1 text-14 text-lightBlue h-full text-left pr-2 ${className || ''}`}
      >
        <span>{label}</span>
        <IconSmallArrow
          color={isActive ? COLORS.orange : COLORS.lightBlue}
          className={`w-2 h-2 ${isActive && sortConfig.order === 'asc' ? 'rotate-90' : 'rotate-[-90deg]'}`}
        />
      </button>
    );
  };

  const storeInvoices = useMemo(() => {
    return invoices?.results?.map((invoice) => {
      return (
        <SingleInvoiceRow
          key={invoice.invoice_id}
          invoice={invoice}
          mode="list"
          onSubmitClick={() => handleSubmitInvoice(invoice)}
          onPreviewClick={() => {
            setActiveInvoice(invoice);
            setInvoiceMode('preview');
          }}
        />
      );
    });
  }, [handleSubmitInvoice, invoices]);

  const hasInvoices = Array.isArray(invoices?.results) && invoices.results.length > 0;

  return (
    <GlobalModal
      open={isOpen}
      width={1044}
      minHeight={902}
      maxHeight={902}
      titleLeft
      overflowHidden={false}
      title={activeInvoice ? 'View Invoice' : `Invoice History (${periodOfInvoices})`}
      headerSelectBar={
        !activeInvoice ? (
          <div className="flex w-full gap-x-1 flex-nowrap items-center">
            <SlhSelect
              data={partnersList}
              value={selectedModalPartner}
              onChange={handlePartnerFilterChange}
              placeholder="SLH"
              isWithAll
              wrapperClassName="min-w-[200px]"
              triggerClassName="!h-[44px] border-none"
            />
            <SelectOption
              onSelect={(value) => handleStatusFilterChange(value)}
              dropdownStyle={{ padding: '16px', background: 'white' }}
              required
              suffixIcon={<IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />}
              className="h-[44px] text-base font-medium base-block rounded-lg  max-w-[200px]"
              placeholder="Status"
              popupClassName="min-w-[180px]"
              value={statusFilter === 'all' ? undefined : statusFilter}
              dropdownRender={(originalNode) => originalNode}
              optionRender={(option) => (
                <div
                  className={`flex items-center gap-x-2 px-4 py-2 m-0 bg-white border rounded-xl ${statusFilter === option.value ? 'border-orange' : 'border-lightGray'}`}
                >
                  <span
                    className={`w-6 h-6 border rounded-full border-orange ${statusFilter === option.value ? 'border-[6px]' : 'border-1'}`}
                  ></span>
                  <p className="font-14 flex-1">{option.label}</p>
                </div>
              )}
              options={[
                { label: 'All', value: 'all' },
                { label: 'Submitted', value: 'ready' },
                { label: 'Pending', value: 'pending' },
              ]}
            />
            {role === ROLE_TYPES.admin && (
              <SelectOption
                onSelect={(value) => handlePeriodChange(value)}
                dropdownStyle={{ padding: '16px', background: 'white' }}
                required
                suffixIcon={
                  <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
                }
                className="h-[44px] text-base font-medium base-block rounded-lg max-w-[200px]"
                placeholder="Weekly"
                popupClassName="min-w-[160px]"
                defaultValue={periodOfInvoices}
                dropdownRender={(originalNode) => originalNode}
                optionRender={(option) => (
                  <div
                    className={`flex items-center gap-x-2 px-4 py-2 m-0 bg-white border rounded-xl ${periodOfInvoices === option.value ? 'border-orange' : 'border-lightGray'}`}
                  >
                    <span
                      className={`w-6 h-6 border rounded-full border-orange ${periodOfInvoices === option.value ? 'border-[6px]' : 'border-1'}`}
                    ></span>
                    <p className="font-14 flex-1">{option.label}</p>
                  </div>
                )}
                options={[
                  { label: 'Weekly', value: 'weekly' },
                  { label: 'Monthly', value: 'monthly' },
                ]}
              />
            )}
            {isFiltersActive && (
              <SmallButton onClick={handleResetFilters} label="Reset All" icon={<IconReset />} />
            )}
          </div>
        ) : null
      }
      onClose={onClose}
      partnerName={partnerName}
      partnerAddress={partnerAddress}
    >
      <div className="flex flex-col p-[30px] pt-6 h-full min-h-0 flex-1">
        {(!activeInvoice || invoiceMode === 'edit') && (
          <div className="flex items-center w-full pb-4 border-b border-color-divider">
            {renderSortableHeader('N', 'invoice_id', 'w-[10%]')}
            {renderSortableHeader('Start Date', 'week_start', 'w-[12%]')}
            {renderSortableHeader('End Date', 'week_end', 'w-[12%]')}
            {renderSortableHeader('SLH', 'partner_name', 'w-[19%]')}
            {renderSortableHeader('Active Residents', 'active_clients', 'w-[10%]')}
            {renderSortableHeader('Invoice Total', 'total', 'w-[10%]')}
            {renderSortableHeader('Status', 'status', 'w-[9%]')}
            <p className="w-[18%] text-14 text-lightBlue flex items-center justify-start text-left pr-2">Actions</p>
          </div>
        )}
        {activeInvoice ? (
          <div className="flex flex-col flex-1 min-h-0">
            <DynamicRows
              invoice={activeInvoice}
              isEditMode={invoiceMode === 'edit'}
              onCancel={handleBackToPreview}
            />
            {role === ROLE_TYPES.slh_manager && invoiceMode === 'preview' && (
              <div className="flex items-center justify-end gap-x-3 border-t border-color-divider pt-4 mt-auto">
                <IconDownload
                  onClick={openActiveInvoiceFile}
                  color={COLORS.darkGrey}
                  className={`w-5 h-5 ${activeInvoiceStatus === 'pending' ? 'cursor-pointer' : 'opacity-40 pointer-events-none'}`}
                />
                <SmallButton label="Back" onClick={handleBackToList} />
                <SmallButton
                  label="Edit"
                  onClick={() => setInvoiceMode('edit')}
                  disabled={activeInvoiceStatus !== 'pending'}
                />
                <OrangeButton
                  label="Submit"
                  onClick={handleSubmitActiveInvoice}
                  disabled={activeInvoiceStatus !== 'pending'}
                  btnClassName=" !text-xs !px-3"
                />
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto">
            {hasInvoices ? (
              storeInvoices
            ) : (
              <div className="flex flex-col items-center justify-center py-10 gap-y-3">
                <img src={hockeyGoalImage} alt="No invoices" className="w-[200px] h-[129px] object-contain" />
                <p className="text-14 text-black uppercase">No invoices</p>
              </div>
            )}
          </div>
        )}
        {invoices.pageCount > 1 && !activeInvoice && (
          <Pagination
            className="self-center mt-4 shrink-0"
            onChange={(page) => handlePaginationChange(page)}
            current={currentPage || invoices.currentPage}
            pageCount={invoices.pageCount}
            pageSize={invoices.pageSize}
            showSizeChanger={false}
            total={invoices.count}
          />
        )}
      </div>
    </GlobalModal>
  );
};
